<script lang="ts" setup>
import { computed, onBeforeMount, ref } from 'vue';
import { requirejs } from '~/modules/require';
import { useTranslations } from '~/composables/translations';

import vuIcon from '@ds/vuekit/src/components/vu-icon.vue';

// PROPS
interface Props {
  hasShipping: boolean;
  services: any[];
}

const props = withDefaults(defineProps<Props>(), {
  hasShipping: false,
  services: undefined,
});

// NLS
const { $i18n } = useTranslations('pages/lab/showroom');

// VARIABLES
const ProductTranslation = ref();

// COMPUTED
const servicesList = computed(() => {
  const list = [];
  if (props.hasShipping) {
    list.push({
      name: $i18n('ship'),
      icon: getServiceIcon($i18n('ship')),
    });
  }

  list.push(
    ...props.services.map((s) => ({
      name: s.additionalServiceName,
      icon: getServiceIcon(s.additionalServiceName),
    })),
  );

  return list;
});

// METHODS
const getServiceIcon = (serviceName: string) => {
  if (ProductTranslation.value) {
    switch (serviceName) {
      case ProductTranslation.value.ship:
        return 'trolley';
      case ProductTranslation.value['3DModeling']:
        return 'rotation-3d';
      case ProductTranslation.value.Machining:
        return 'manufacturing-substractive';
      case ProductTranslation.value.Inspection:
        return 'part-search';
      case ProductTranslation.value.STLRepair:
        return 'tools';
      case ProductTranslation.value.SurfaceTreatment:
        return '3d-object';
      case ProductTranslation.value.GeometryOptimization:
        return '3d-object-reset';
      default:
        return 'cog';
    }
  }

  return 'cog';
};

// MOUNTED
onBeforeMount(async () => {
  const [i18nProducts] = await requirejs<[any]>(['i18n!DS/MP3DPDAO/assets/nls/Products']);

  ProductTranslation.value = {
    ...i18nProducts,
  };
});
</script>

<template>
  <div class="pb-10px">
    <div class="showroom-section-subtitle text--uppercase">
      {{ $i18n('additionalServices') }}
    </div>
    <div class="showroom-container">
      <div v-if="servicesList.length" class="grid additional-services">
        <template v-for="(service, i) in servicesList">
          <div v-if="service.name" :key="i" class="service-tile flex">
            <vu-icon class="service-icon" :icon="service.icon" />
            <div class="additional-item-text align-self-center flex-grow-1">
              {{
                ProductTranslation && ProductTranslation[service.name.replace(' ', '')]
                  ? ProductTranslation[service.name.replace(' ', '')]
                  : service.name
              }}
            </div>
          </div>
        </template>
      </div>
      <div v-else class="additional-services no-item">
        {{ $i18n('noServiceYet') }}
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '/assets/scss/variables' as variables;
@forward '/assets/scss/skeletons';

.additional-services {
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  grid-column-gap: 10px;
  grid-row-gap: 10px;
}

.service-tile {
  background: white;
  border: #d1d4d4 solid 1px;
  border-radius: 3px;
}

.service-tile .service-icon {
  min-width: 44px;
  width: 50px;
  height: 50px;
  background-color: #368ec4;
  color: #ffffff;
  border-radius: 50%;
  font-size: 31px;
  margin: 7px;
  padding: 3px;
}

.service-tile .additional-item-text {
  font-family: '3ds';
  text-align: center;
  align-self: center;
}

@media (max-width: 765px) {
  .service-tile .additional-item-text {
    text-align: start;
  }
}

.showroom-list {
  overflow: hidden; // Hide unwanted borders for each flex row
}

.showroom-container {
  background-color: transparent;
  border: none;
  padding: 10px 0;
}

.showroom-list-element {
  width: 200px;
  margin-left: -21px;
  margin-right: 21px;
  border-left: 1px solid var(--grey-4);

  .disc::before {
    content: '';
    display: inline-block;
    border-radius: 50%;
    background: var(--blue-3);
    width: 12px;
    height: 12px;
    transform: scale(0.5, 0.5);
    vertical-align: middle;
    margin-bottom: 1.5px;
  }

  .text {
    position: relative;
    word-break: break-all;
    text-align: start;
  }
}
</style>
